import isEqual from 'fast-deep-equal/react';
import {BehaviorSubject, filter, map, Observable, skip} from 'rxjs';
import {v4 as uuid} from 'uuid';
import {Conversation} from './conversation';
import {METRIC} from './metrics';
import {
  ConversationEvent,
  ConversationMessage,
  ConversationParticipant,
} from './types';

interface MessageDescriptor {
  id: string;
  description: string;
  defaultMessage: string;
}

export type ConversationLogItemStarted = {
  id: string;
  timestamp: number;
  name: 'conversation-started';
};

export type ConversationLogItemEnded = {
  id: string;
  timestamp: number;
  name: 'conversation-ended';
};

export type MetaEventStyle = 'caption' | 'body';

export type ConversationLogItemMetaEvent = {
  id: string;
  timestamp: number;
  name: 'conversation-meta';
  message: MessageDescriptor;
  values?: Record<string, string | number | boolean>;
  style?: MetaEventStyle;
};

export type ConversationLogItemParticipantJoined = {
  id: string;
  timestamp: number;
  name: 'conversation-participant-joined';
  participant: ConversationParticipant;
};

export type ConversationLogItemParticipantLeft = {
  id: string;
  timestamp: number;
  name: 'conversation-participant-left';
  participant: ConversationParticipant;
};

export type ConversationLogItemMessageAdded = {
  id: string;
  timestamp: number;
  name: 'conversation-message-added';
  message: ConversationMessage;
};

export type CustomComponent = {
  id: string;
  timestamp: number;
  name: 'conversation-custom-component';
};

export type CallToActionComponentLogItem = CustomComponent & {
  componentType: 'call-to-action';
  data: {
    message?: MessageDescriptor;
    buttonText: MessageDescriptor;
    values?: Record<string, string | number | boolean>;
    onPress: () => void;
  };
};

export type ConversationLogItemCustomComponent = CallToActionComponentLogItem;

export type ConversationLogItem =
  | ConversationLogItemStarted
  | ConversationLogItemEnded
  | ConversationLogItemMetaEvent
  | ConversationLogItemParticipantJoined
  | ConversationLogItemParticipantLeft
  | ConversationLogItemMessageAdded
  | ConversationLogItemCustomComponent;

export class ConversationLog {
  private conversation: Conversation;

  private log$: BehaviorSubject<Record<string, ConversationLogItem>>;

  private sortedLog$: BehaviorSubject<ConversationLogItem[]> =
    new BehaviorSubject<ConversationLogItem[]>([]);

  constructor(conversation: Conversation) {
    this.conversation = conversation;

    // the conversation started event should probably
    // eventually come from conversation metadata about
    // when it was created
    const conversationStarted: ConversationLogItemStarted = {
      name: 'conversation-started',
      id: 'conversation-started',
      timestamp: Date.now(),
    };

    const fromConversation = Object.values(this.conversation.messages).reduce(
      (log, message) => {
        const {id, timestamp} = message;
        log[message.id] = {
          name: 'conversation-message-added',
          id,
          timestamp,
          message,
        };

        // use the earliest message timestamp as started timestamp
        if (timestamp <= conversationStarted.timestamp) {
          conversationStarted.timestamp = timestamp - 1;
        }

        return log;
      },
      {} as Record<string, ConversationLogItem>,
    );

    this.log$ = new BehaviorSubject({
      [conversationStarted.id]: conversationStarted,
      ...fromConversation,
    });

    this.log$.asObservable().subscribe((log) => {
      this.sortedLog$.next(
        Object.values(log).sort((a, b) => a.timestamp - b.timestamp),
      );
    });

    this.conversation
      .eventsObservable()
      .pipe(map(this.eventToItem), filter(this.filterNullOrUndefined))
      .subscribe(this.addItem);
  }

  get = (): ConversationLogItem[] => {
    return this.sortedLog$.getValue();
  };

  getUnsorted = (): Record<string, ConversationLogItem> => {
    return this.log$.getValue();
  };

  observable = (): Observable<ConversationLogItem[]> => {
    return this.sortedLog$.asObservable().pipe(
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  set = (log: ConversationLogItem[]): void => {
    this.conversation.storageVersion += 1;
    this._setWithoutUpdatingStorage(log);
  };

  // ideally this would be private, but for now conversation needs
  // to call it
  _setWithoutUpdatingStorage = (log: ConversationLogItem[]): void => {
    const next = log.reduce((next, item) => {
      next[item.id] = item;
      return next;
    }, {} as Record<string, ConversationLogItem>);

    this.log$.next(next);
  };

  appendMetaEvent = ({
    message,
    values,
    id = uuid(),
    style = 'caption',
    // an explicit timestamp allows a reconstructed marker (e.g. on resume) to
    // be placed at the point it originally happened instead of at the end
    timestamp = Date.now(),
  }: {
    message: MessageDescriptor;
    values?: Record<string, string | number | boolean>;
    id?: string;
    style?: MetaEventStyle;
    timestamp?: number;
  }): void => {
    const item: ConversationLogItemMetaEvent = {
      name: 'conversation-meta',
      timestamp,
      id,
      message,
      values,
      style,
    };
    this.addItem(item);
  };

  appendCallToAction = ({
    id = uuid(),
    data,
  }: {
    id?: string;
    data: CallToActionComponentLogItem['data'];
  }): void => {
    const item: ConversationLogItemCustomComponent = {
      name: 'conversation-custom-component',
      timestamp: Date.now(),
      id,
      componentType: 'call-to-action',
      data,
    };
    this.addItem(item);
  };

  private addItem = (item: ConversationLogItem): void => {
    const next = {...this.log$.getValue()};

    const existing = next[item.id];
    // Message timestamps can begin as optimistic client timestamps and later be
    // reconciled with the server. Other log items keep their original
    // timestamps because the client generates new timestamps for replayed
    // events.
    const timestamp =
      item.name === 'conversation-message-added'
        ? item.message.timestamp
        : existing?.timestamp ?? item.timestamp;

    const nextItem = {...item, timestamp};
    const existingItem = {...existing, timestamp};

    // avoid unnecessary updates
    if (isEqual(nextItem, existingItem)) {
      return;
    }

    next[item.id] = nextItem;

    // use the earliest message timestamp as started timestamp
    if (item.name === 'conversation-message-added') {
      const conversationStarted = next['conversation-started'];
      if (
        conversationStarted?.name === 'conversation-started' &&
        timestamp < conversationStarted.timestamp
      ) {
        next[conversationStarted.id] = {
          ...conversationStarted,
          timestamp: timestamp - 1,
        };
      }
    }

    this.conversation.storageVersion += 1;
    this.log$.next(next);
  };

  private eventToItem = (
    event: ConversationEvent,
  ): ConversationLogItem | null => {
    switch (event.name) {
      case 'conversation-message-added':
      case 'conversation-message-updated':
        let message: ConversationMessage | undefined | null;

        if (event.nonce) {
          message = this.conversation.getMessage(event.nonce);
        }
        if (!message) {
          message = this.conversation.getMessage(event.messageId);
        }
        if (!message) {
          message = event.message;
        }

        if (!message) {
          const tags = {
            conversationId: this.conversation.id,
            messageId: event.messageId,
            clientType: this.conversation.client.type,
          };
          this.conversation.client.errors.reportError(
            '[conversation-log] missing message for new message event',
            {tags},
          );
          this.conversation.client.metrics.increment(
            METRIC.log_missing_message,
            {clientType: this.conversation.client.type},
          );
          this.conversation.client.analytics.track(
            METRIC.log_missing_message,
            tags,
          );
          return null;
        }

        if (
          message.id.startsWith('ignore') ||
          message.nonce?.startsWith('ignore')
        ) {
          return null;
        }

        // favor the message id + timestamp over the event timestamp
        return {
          name: 'conversation-message-added',
          // to make local items stable, always prefer the nonce if available
          id: message.nonce || message.id,
          timestamp: message.timestamp,
          message,
        };
      case 'conversation-participant-joined':
      case 'conversation-participant-left':
        return {
          name: event.name,
          id: event.id,
          timestamp: event.timestamp,
          participant: event.participant,
        };
      case 'conversation-ended':
        return {
          name: 'conversation-ended',
          // always have a single conversation-ended event
          id: 'conversation-ended',
          timestamp: event.timestamp,
        };
      default:
    }

    return null;
  };

  private filterNullOrUndefined = <T>(
    value: T | null | undefined,
  ): value is T => {
    return !!value;
  };
}
